import { ColumnDef } from '@tanstack/react-table'
import type { RunbookTask } from '@/types/entities'
import { truncateToMinuteFloor } from '@/lib/dateUtils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Plus, Trash2, ArrowUp, Save, Copy, ImageIcon, X } from 'lucide-react'
import { useState, useRef, useEffect, memo } from 'react'
import { useToast } from '@/hooks/use-toast'
import { validateDependencies as validateDeps } from './utils/validateDependencies'
import { RUNBOOK_MODES, SELECTABLE_METHODS, AutomationOptionLabel, findMode, findMethod } from './utils/runbookAutomation'
import { BranchBadge } from './utils/runbookOptions'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { LucideIcon } from 'lucide-react'
import { TranslatableText } from '@/components/ui/TranslatableText'

// Inline editable text cell with auto-expanding textarea
const EditableTextCell = memo(function EditableTextCell({
    value,
    onUpdate,
    placeholder,
    textColor,
    readOnly = false,
    showCopyIcon = false,
    translatable = false,
}: {
    value: string
    onUpdate: (value: string) => void
    placeholder: string
    textColor?: string
    readOnly?: boolean
    showCopyIcon?: boolean
    translatable?: boolean
}) {
    const { toast } = useToast()
    const [isEditing, setIsEditing] = useState(false)
    const [localValue, setLocalValue] = useState(value || '')
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus()
            textareaRef.current.select()
            // Auto-expand
            textareaRef.current.style.height = 'auto'
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
        }
    }, [isEditing])

    useEffect(() => {
        // Only sync if not currently editing to prevent jumping
        if (!isEditing) {
            setLocalValue(value || '')
        }
    }, [value, isEditing])

    const handleSave = () => {
        if (localValue !== (value || '')) {
            onUpdate(localValue)
        }
        setIsEditing(false)
    }

    const handleCancel = () => {
        setLocalValue(value || '')
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Stop propagation to prevent Swiper from capturing keyboard events
        e.stopPropagation()
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        }
        // Don't prevent default for other keys - allow normal typing
    }

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // Stop propagation to prevent Swiper from interfering
        e.stopPropagation()
        setLocalValue(e.target.value)
        // Auto-expand
        e.target.style.height = 'auto'
        e.target.style.height = e.target.scrollHeight + 'px'
    }

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (value) {
            try {
                await navigator.clipboard.writeText(value)
                toast({
                    title: 'Copied',
                    description: 'Text copied to clipboard',
                })
            } catch (err) {
                toast({
                    title: 'Error',
                    description: 'Failed to copy text',
                    variant: 'destructive',
                })
            }
        }
    }

    return (
        <div
            className="w-full flex items-start gap-1"
            onClick={(e) => {
                if (!isEditing && !readOnly) {
                    e.stopPropagation()
                    setIsEditing(true)
                }
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
        >
            {isEditing && !readOnly ? (
                <textarea
                    ref={textareaRef}
                    value={localValue}
                    onChange={handleChange}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    onKeyPress={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onInput={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    className="min-h-8 w-full text-sm border rounded-md px-3 py-2 resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation()
                    }}
                    onMouseUp={(e) => {
                        e.stopPropagation()
                    }}
                    rows={1}
                    autoFocus
                />
            ) : (
                <>
                    <div
                        className={`min-h-8 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center ${readOnly ? '' : 'cursor-text hover:border-ring transition-colors'}`}
                        style={{ minWidth: 0 }}
                    >
                        {localValue ? (
                            <span className="text-sm w-full whitespace-pre-wrap break-words">{translatable ? <TranslatableText text={localValue} /> : localValue}</span>
                        ) : (
                            <span className={`text-sm w-full whitespace-pre-wrap break-words ${textColor || 'text-muted-foreground'}`}>{placeholder}</span>
                        )}
                    </div>
                    {showCopyIcon && value && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 mt-1 flex-shrink-0"
                            onClick={handleCopy}
                            title="Copy to clipboard"
                        >
                            <Copy className="h-3.5 w-3.5 text-blue-600" />
                        </Button>
                    )}
                </>
            )}
        </div>
    )
})

// Inline editable dependencies cell with validation
const EditableDependenciesCell = memo(function EditableDependenciesCell({
    value,
    onUpdate,
    placeholder,
    allTasks,
    currentTaskNo,
}: {
    value: string
    onUpdate: (value: string) => void
    placeholder: string
    allTasks: (RunbookTask & { _index: number })[]
    currentTaskNo: number | null
}) {
    const [isEditing, setIsEditing] = useState(false)
    const [localValue, setLocalValue] = useState(value || '')
    const [error, setError] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    useEffect(() => {
        setLocalValue(value || '')
        setError(null)
    }, [value])

    const validateDependencies = (input: string) => validateDeps(input, allTasks, currentTaskNo)

    const handleSave = () => {
        const validationError = validateDependencies(localValue)
        if (validationError) {
            setError(validationError)
            // Keep focus on input if empty/invalid - prevent navigation away
            if (inputRef.current) {
                inputRef.current.focus()
            }
            return // Don't save if invalid
        }

        if (localValue !== (value || '')) {
            onUpdate(localValue)
        }
        setError(null)
        setIsEditing(false)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Check if the new focus target is outside this component
        // If field is empty, prevent blur and keep focus
        const validationError = validateDependencies(localValue)
        if (validationError) {
            e.preventDefault()
            e.stopPropagation()
            // Keep focus on the input
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus()
                }
            }, 0)
            return
        }
        // Only call handleSave if valid
        handleSave()
    }

    const handleCancel = () => {
        setLocalValue(value || '')
        setError(null)
        setIsEditing(false)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        const newValue = e.target.value
        setLocalValue(newValue)

        // Validate on change to show errors in real-time
        const validationError = validateDependencies(newValue)
        setError(validationError)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        } else if (e.key === 'Tab') {
            // Prevent Tab navigation if field is empty/invalid
            const validationError = validateDependencies(localValue)
            if (validationError) {
                e.preventDefault()
                e.stopPropagation()
                // Keep focus on input
                if (inputRef.current) {
                    inputRef.current.focus()
                }
            }
            // If valid, allow Tab to proceed normally
        }
    }

    return (
        <div className="w-full">
            <div
                className="w-full"
                onClick={(e) => {
                    if (!isEditing) {
                        e.stopPropagation()
                        setIsEditing(true)
                    }
                }}
                onMouseDown={(e) => {
                    e.stopPropagation()
                }}
            >
                {isEditing ? (
                    <div className="w-full">
                        <input
                            ref={inputRef}
                            type="text"
                            value={localValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            onKeyPress={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                            onInput={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            className={`h-8 w-full text-sm border rounded-md px-3 py-2 focus-visible:outline-none focus-visible:ring-2 ${error
                                ? 'border-destructive focus-visible:ring-destructive'
                                : 'focus-visible:ring-ring'
                                }`}
                            onClick={(e) => {
                                e.stopPropagation()
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation()
                            }}
                            placeholder={placeholder}
                            autoFocus
                        />
                        {error && (
                            <div className="text-xs text-destructive mt-1 px-1">
                                {error}
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className="h-8 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center cursor-text hover:border-ring transition-colors"
                        style={{ minWidth: 0 }}
                    >
                        <span className="text-sm truncate w-full">{localValue || placeholder}</span>
                    </div>
                )}
            </div>
        </div>
    )
})

// Inline editable number cell
const EditableNumberCell = memo(function EditableNumberCell({
    value,
    onUpdate,
    placeholder,
}: {
    value: number | null
    onUpdate: (value: number | null) => void
    placeholder: string
}) {
    const [isEditing, setIsEditing] = useState(false)
    const [localValue, setLocalValue] = useState(value?.toString() || '')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    useEffect(() => {
        setLocalValue(value?.toString() || '')
    }, [value])

    const handleSave = () => {
        const newValue = localValue === '' ? null : parseInt(localValue, 10)
        if (newValue !== value) {
            onUpdate(isNaN(newValue as number) ? null : newValue)
        }
        setIsEditing(false)
    }

    const handleCancel = () => {
        setLocalValue(value?.toString() || '')
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Stop propagation to prevent Swiper from capturing keyboard events
        e.stopPropagation()
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        }
        // Don't prevent default for other keys - allow normal typing
    }

    return (
        <div
            className="w-full"
            onClick={(e) => {
                if (!isEditing) {
                    e.stopPropagation()
                    setIsEditing(true)
                }
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
        >
            {isEditing ? (
                <Input
                    ref={inputRef}
                    type="number"
                    value={localValue}
                    onChange={(e) => {
                        e.stopPropagation()
                        setLocalValue(e.target.value)
                    }}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="h-8 w-full text-sm"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    min="0"
                />
            ) : (
                <div
                    className="h-8 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center cursor-text hover:border-ring transition-colors"
                    style={{ minWidth: 0 }}
                >
                    <span className="text-sm truncate w-full">{localValue || placeholder}</span>
                </div>
            )}
        </div>
    )
})

/**
 * Compact dropdown cell for enumerated task fields (recovery-automation Mode/Method).
 * Options render as icon + one-or-two-word label so the column stays narrow — this
 * table already has to fit task text, commands and four time columns.
 */
const EditableSelectCell = memo(function EditableSelectCell({
    value,
    options,
    onUpdate,
    iconOnly = false,
}: {
    value: string
    options: { value: string; label: string; icon?: LucideIcon }[]
    onUpdate: (value: string) => void
    /** Trigger shows the icon alone, with the words on hover — the table is short of width. */
    iconOnly?: boolean
}) {
    const selected = options.find((o) => o.value === value)
    const SelectedIcon = selected?.icon
    const trigger = iconOnly ? (
        <SelectTrigger className="h-8 w-[58px] px-1.5 text-sm [&>svg]:h-3 [&>svg]:w-3" aria-label={selected?.label}>
            {SelectedIcon
                ? <SelectedIcon className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
                : <span className="text-muted-foreground">-</span>}
        </SelectTrigger>
    ) : (
        <SelectTrigger className="h-8 text-sm px-2">
            <SelectValue />
        </SelectTrigger>
    )

    return (
        <div
            className="w-full"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <Select value={value} onValueChange={onUpdate}>
                {iconOnly && selected?.label ? (
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                            <TooltipContent>{selected.label}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : trigger}
                <SelectContent>
                    {options.map((o) => {
                        const Icon = o.icon
                        return (
                            <SelectItem key={o.value} value={o.value}>
                                <span className="inline-flex items-center gap-1.5 min-w-0">
                                    {Icon ? <Icon className="h-3.5 w-3.5 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden /> : null}
                                    <span className="truncate">{o.label}</span>
                                </span>
                            </SelectItem>
                        )
                    })}
                </SelectContent>
            </Select>
        </div>
    )
})

export interface RunbookTableProps {
    tasks: RunbookTask[]
    onTasksChange: (tasks: RunbookTask[]) => void
    onSave: () => void
    autoUpdate: boolean
    onAutoUpdateChange: (autoUpdate: boolean) => void
    isSaving?: boolean
}

export function createRunbookColumns(
    onTaskChange: (index: number, field: keyof RunbookTask, value: any) => void,
    /** Several fields in one update — two onTaskChange calls in a row lose the first. */
    onTaskPatch: (index: number, patch: Partial<RunbookTask>) => void,
    onAddTask: () => void,
    onInsertTask: (index: number) => void,
    onDeleteTask: (index: number) => void,
    t: (key: string) => string,
    locale: string | undefined,
    allTasks: (RunbookTask & { _index: number })[],
    readOnly: boolean = false
): ColumnDef<RunbookTask & { _index: number }>[] {
    const effectiveLocale = locale || 'en-US'
    const columns: ColumnDef<RunbookTask & { _index: number }>[] = [
        {
            accessorKey: 'taskNo',
            header: t('objects.runbook.taskNo'),
            size: 52,
            minSize: 44,
            maxSize: 60,
            cell: ({ row }) => {
                const taskNo = row.getValue('taskNo') as number | null
                return (
                    <div
                        className="w-full flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <span className="text-sm font-bold text-black">
                            {taskNo ?? '-'}
                        </span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'task',
            header: t('objects.runbook.task'),
            size: 560,
            minSize: 340,
            maxSize: 900,
            cell: ({ row }) => {
                const index = row.original._index
                const task = row.getValue('task') as string
                return (
                    <EditableTextCell
                        value={task || ''}
                        onUpdate={(value) => onTaskChange(index, 'task', value)}
                        placeholder={t('objects.runbook.task')}
                        readOnly={readOnly}
                        showCopyIcon={true}
                        translatable={true}
                    />
                )
            },
        },
        {
            accessorKey: 'commands',
            header: t('objects.runbook.commands'),
            size: 160,
            minSize: 120,
            maxSize: 260,
            cell: ({ row }) => {
                const index = row.original._index
                const commands = row.getValue('commands') as string
                return (
                    <EditableTextCell
                        value={commands || ''}
                        onUpdate={(value) => onTaskChange(index, 'commands', value)}
                        placeholder={t('objects.runbook.commands')}
                        textColor="text-muted-foreground"
                        readOnly={readOnly}
                        showCopyIcon={true}
                    />
                )
            },
        },
        {
            accessorKey: 'imagePath',
            header: t('objects.runbook.image', 'Image'),
            size: 56,
            minSize: 48,
            maxSize: 64,
            cell: ({ row }) => {
                const index = row.original._index
                const imagePath = row.getValue('imagePath') as string | null
                if (readOnly) {
                    return imagePath ? (
                        <div className="flex justify-center">
                            <img
                                src={`/images/${imagePath}`}
                                alt=""
                                className="h-8 w-8 rounded object-cover cursor-pointer hover:opacity-80"
                                onClick={(e) => { e.stopPropagation(); window.open(`/images/${imagePath}`, '_blank') }}
                            />
                        </div>
                    ) : null
                }
                return (
                    <div
                        className="flex justify-center items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        {imagePath ? (
                            <div className="relative group">
                                <img
                                    src={`/images/${imagePath}`}
                                    alt=""
                                    className="h-8 w-8 rounded object-cover cursor-pointer hover:opacity-80"
                                    onClick={() => onTaskChange(index, 'imagePath', '__PICK__')}
                                />
                                <button
                                    className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => onTaskChange(index, 'imagePath', null)}
                                >
                                    <X className="h-2.5 w-2.5" />
                                </button>
                            </div>
                        ) : (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => onTaskChange(index, 'imagePath', '__PICK__')}
                                title={t('objects.runbook.addImage', 'Add image')}
                            >
                                <ImageIcon className="h-4 w-4 text-blue-600" />
                            </Button>
                        )}
                    </div>
                )
            },
        },
        {
            accessorKey: 'duration',
            header: t('objects.runbook.duration'),
            size: 76,
            minSize: 64,
            maxSize: 90,
            cell: ({ row }) => {
                const index = row.original._index
                const duration = row.getValue('duration') as number | null
                if (readOnly) {
                    return (
                        <div className="w-full text-right" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                            <span className="text-sm text-muted-foreground">
                                {duration != null ? `${duration}${t('objects.runbook.minutesShort') || 'm'}` : '-'}
                            </span>
                        </div>
                    )
                }
                return (
                    <EditableNumberCell
                        value={duration}
                        onUpdate={(value) => onTaskChange(index, 'duration', value)}
                        placeholder="0"
                    />
                )
            },
        },
        {
            accessorKey: 'taskOffset',
            header: t('objects.runbook.taskOffset') || 'Task Offset (min)',
            size: 84,
            minSize: 70,
            maxSize: 100,
            cell: ({ row }) => {
                const taskOffset = row.getValue('taskOffset') as number | null
                return (
                    <div
                        className="w-full text-right"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <span className="text-sm text-muted-foreground">
                            {taskOffset !== null && taskOffset !== undefined ? taskOffset : '-'}
                        </span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'startTime',
            header: t('objects.runbook.startTime'),
            size: 124,
            minSize: 108,
            maxSize: 150,
            cell: ({ row }) => {
                const task = row.original
                const taskOffset = task.taskOffset as number | null
                const duration = task.duration as number | null
                
                // Calculate start time from TaskOffset using NOW() as base
                if (taskOffset === null || taskOffset === undefined) {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }

                try {
                    // Use NOW() as base time (minutes only), add TaskOffset (in minutes)
                    const baseTime = truncateToMinuteFloor(new Date())
                    const startTime = new Date(baseTime.getTime() + taskOffset * 60 * 1000)

                    if (isNaN(startTime.getTime())) {
                        return (
                            <div
                                className="w-full"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <span className="text-sm text-muted-foreground">-</span>
                            </div>
                        )
                    }

                    const dateStr = startTime.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                    const timeStr = startTime.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                    return (
                        <div
                            className="w-full whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">
                                {dateStr} {timeStr}
                            </span>
                        </div>
                    )
                } catch {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }
            },
        },
        {
            accessorKey: 'finishTime',
            header: t('objects.runbook.finishTime'),
            size: 124,
            minSize: 108,
            maxSize: 150,
            cell: ({ row }) => {
                const task = row.original
                const taskOffset = task.taskOffset as number | null
                const duration = task.duration as number | null
                
                // Calculate finish time from TaskOffset + Duration using NOW() as base
                if (taskOffset === null || taskOffset === undefined || duration === null || duration === undefined) {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }

                try {
                    // Use NOW() as base time (minutes only), add TaskOffset + Duration (both in minutes)
                    const baseTime = truncateToMinuteFloor(new Date())
                    const finishTime = new Date(baseTime.getTime() + (taskOffset + duration) * 60 * 1000)

                    if (isNaN(finishTime.getTime())) {
                        return (
                            <div
                                className="w-full"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <span className="text-sm text-muted-foreground">-</span>
                            </div>
                        )
                    }

                    const dateStr = finishTime.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                    const timeStr = finishTime.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                    return (
                        <div
                            className="w-full whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">
                                {dateStr} {timeStr}
                            </span>
                        </div>
                    )
                } catch {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }
            },
        },
        {
            // Automation, Method and Option were three columns totalling 260px that mostly showed a
            // dash: Method is not editable at all while the mode is manual, and Option is empty
            // unless the task sits on a branch. Meanwhile Task — the column people actually read —
            // was being crushed, because the declared widths total far more than the dialog is wide
            // and Task is the only one that gives. One column at 92px hands 168px back to it.
            //
            // Nothing is lost. The mode select is always present; the method select appears beside
            // it only when it can do something (mode !== manual); the branch badge and its planned
            // toggle drop underneath only when the task actually has a branch.
            id: 'automation',
            header: t('objects.runbook.automation'),
            size: 92,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => {
                const index = row.original._index
                const mode = ((row.original.mode as string | null) || 'manual').toLowerCase()
                const method = ((row.original.method as string | null) || 'manual').toLowerCase()
                const modeOption = findMode(mode)
                const methodOption = findMethod(method)
                const group = (row.original.optionGroup || '').trim()
                const name = (row.original.optionName || '').trim()

                const modeCell = readOnly ? (
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex" aria-label={t(modeOption.labelKey)}>
                                    <modeOption.icon className={`h-4 w-4 ${mode === 'manual' ? 'text-muted-foreground' : 'text-purple-700 dark:text-purple-300'}`} aria-hidden />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>{t(modeOption.labelKey)}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <EditableSelectCell
                        value={mode}
                        options={RUNBOOK_MODES.map(m => ({ value: m.value, label: t(m.labelKey), icon: m.icon }))}
                        iconOnly
                        onUpdate={(v) => onTaskPatch(index, {
                            mode: v,
                            // A manual task has no handler; keep Method consistent.
                            ...(v === 'manual' ? { method: 'manual' } : {}),
                        })}
                    />
                )

                const methodCell = mode === 'manual' ? null : readOnly ? (
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex" aria-label={t(methodOption.labelKey)}>
                                    <methodOption.icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>{t(methodOption.labelKey)}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <EditableSelectCell
                        value={method}
                        options={SELECTABLE_METHODS.map(m => ({ value: m.value, label: t(m.labelKey), icon: m.icon }))}
                        iconOnly
                        onUpdate={(v) => onTaskChange(index, 'method', v)}
                    />
                )

                return (
                    <div
                        className="w-full flex flex-col gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-1">
                            {modeCell}
                            {methodCell}
                        </div>
                        {group && name && (
                            <div className="flex flex-col gap-0.5">
                                <BranchBadge name={name} isDefault={!!row.original.optionDefault} />
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="text-[11px] text-muted-foreground hover:text-foreground text-left truncate"
                                        onClick={() => onTaskChange(index, 'optionDefault', !row.original.optionDefault)}
                                        title={t('objects.runbook.optionDefault')}
                                    >
                                        {row.original.optionDefault
                                            ? t('objects.runbook.plannedBranch')
                                            : t('objects.runbook.makePlanned')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )
            },
        },
        {
            accessorKey: 'dependencies',
            header: t('objects.runbook.dependencies'),
            size: 104,
            minSize: 88,
            maxSize: 130,
            cell: ({ row }) => {
                const index = row.original._index
                const taskNo = row.original.taskNo
                const dependencies = row.getValue('dependencies') as string
                const isFirstTask = taskNo === 1

                if (isFirstTask) {
                    return (
                        <div
                            className="h-8 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm flex items-center"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">{t('objects.runbook.noDependencies')}</span>
                        </div>
                    )
                }

                if (readOnly) {
                    return (
                        <div
                            className="h-8 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm flex items-center"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">{dependencies || '-'}</span>
                        </div>
                    )
                }

                return (
                    <EditableDependenciesCell
                        value={dependencies || ''}
                        onUpdate={(value) => onTaskChange(index, 'dependencies', value)}
                        placeholder={t('objects.runbook.dependencies')}
                        allTasks={allTasks}
                        currentTaskNo={taskNo}
                    />
                )
            },
        },
    ]

    // Only add actions column if not read-only
    if (!readOnly) {
        columns.push({
            id: 'actions',
            header: () => (
                <div className="text-right w-full">
                    {t('common.actions')}
                </div>
            ),
            size: 88,
            minSize: 76,
            maxSize: 110,
            cell: ({ row }) => {
                const index = row.original._index
                return (
                    <div
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onInsertTask(index)
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                            }}
                            title={t('objects.runbook.insertAbove')}
                        >
                            <ArrowUp className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-red-500 hover:text-red-600"
                            onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onDeleteTask(index)
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                            }}
                            title={t('objects.runbook.delete')}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        })
    }

    return columns
}

